// El panel para elegir los valores de UNA propiedad (Talle, Color…) —
// ADR-2026-10-06-variantes-por-propiedades.
//
// Copia el comportamiento de Tienda Nube (panel lateral, valores sugeridos por grupo
// con "seleccionar todos", la lista de elegidos para ordenar y renombrar) con tres
// cambios para que sea más corto:
//  - Las sugerencias son PILLS, no filas con checkbox: los seis talles de adulto
//    entran en una línea y se eligen sin scrollear.
//  - "S, M, L" en el campo de agregar suma los tres de una vez.
//  - El pie dice cuántas variantes salen (y cuántas se quitan) antes de aplicar.
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, Check, GripVertical, Plus, Search, X } from 'lucide-react';
import { boton } from './botones';
import { buscarColores, colorDeTiendanube, esAtributoColor } from './coloresTiendanube';
import {
  TALLES,
  cuantasVariantes,
  esPropiedadTalle,
  mismoValor,
  partirValores,
  type Propiedad,
  type ValorDePropiedad,
} from './propiedades';

/** Los colores que más se usan, primero. Sólo los que existen tal cual en la lista de Tienda Nube. */
const COLORES_COMUNES = [
  'Negro', 'Blanco', 'Gris', 'Beige', 'Marrón', 'Azul', 'Celeste', 'Verde',
  'Rojo', 'Bordó', 'Rosa', 'Violeta', 'Amarillo', 'Naranja',
]
  .map((n) => colorDeTiendanube(n))
  .filter((c): c is NonNullable<typeof c> => c !== null);

export default function EditorDePropiedad({
  propiedades,
  indice,
  nombreInicial,
  sugeridos,
  variantesAntes,
  maxVariantes,
  colorComoTexto,
  onAplicar,
  onCerrar,
}: {
  /** Todas las propiedades del producto, para contar las combinaciones. */
  propiedades: readonly Propiedad[];
  /** Cuál se edita; `null` = una nueva. */
  indice: number | null;
  /** Al crear una desde una sugerencia ("Talle"), ya viene nombrada: se saltea elegir el nombre. */
  nombreInicial?: string;
  /** Nombres que se ofrecen para una propiedad nueva (por rubro, sin las que ya están). */
  sugeridos: readonly string[];
  variantesAntes: number;
  maxVariantes: number | null;
  /** Exportando a carpetas (ZIP) el color es texto libre: no hay círculo de Tienda Nube. */
  colorComoTexto: boolean;
  /** Devuelve el error, o `null`: con error el panel queda abierto y lo dice (review #168). */
  onAplicar: (propiedades: Propiedad[]) => Promise<string | null>;
  onCerrar: () => void;
}) {
  const actual = indice === null ? null : propiedades[indice] ?? null;
  const [nombre, setNombre] = useState(actual?.nombre ?? nombreInicial ?? '');
  const [eligiendoNombre, setEligiendoNombre] = useState(nombre.trim() === '');
  const [valores, setValores] = useState<ValorDePropiedad[]>(actual?.valores ?? []);
  const [texto, setTexto] = useState('');
  const [busquedaColor, setBusquedaColor] = useState('');
  const [aplicando, setAplicando] = useState(false);
  const [errorAlAplicar, setErrorAlAplicar] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState<number | null>(null);
  const campoRef = useRef<HTMLInputElement | null>(null);

  // Mientras guarda, el panel no se cierra (review #168): cerrarlo y volver a aplicar superponía dos
  // guardados sobre las mismas variantes.
  const cerrar = () => {
    if (!aplicandoRef.current) onCerrar();
  };
  const aplicandoRef = useRef(false);
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !aplicandoRef.current) onCerrar();
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  const esTalle = esPropiedadTalle(nombre);
  const esColor = esAtributoColor(nombre);
  const queSon = esTalle ? 'talles' : esColor ? 'colores' : 'valores';
  const unoDe = esTalle ? 'talle' : esColor ? 'color' : 'valor';

  const elegido = (v: string) => valores.some((x) => mismoValor(x.texto, v));
  /**
   * Un valor que vuelve (destildar "S" y tildarla de nuevo) recupera su identidad de antes: así su
   * variante sigue siendo la misma, con su stock y sus fotos, y el pie no avisa que se va a borrar
   * (review #168).
   */
  const nuevoValor = (texto: string, ya: readonly ValorDePropiedad[]): ValorDePropiedad => {
    const previo = actual?.valores.find(
      (o) => o.original !== null && mismoValor(o.original, texto) && !ya.some((x) => x.original === o.original),
    );
    return { texto, original: previo?.original ?? null };
  };
  const alternar = (v: string) =>
    setValores((vs) => (vs.some((x) => mismoValor(x.texto, v)) ? vs.filter((x) => !mismoValor(x.texto, v)) : [...vs, nuevoValor(v, vs)]));
  const sumar = (lista: readonly string[]) =>
    setValores((vs) => {
      const salida = [...vs];
      for (const v of lista) if (!salida.some((x) => mismoValor(x.texto, v))) salida.push(nuevoValor(v, salida));
      return salida;
    });

  const agregarEscrito = () => {
    const nuevos = partirValores(texto);
    if (nuevos.length === 0) return;
    sumar(nuevos);
    setTexto('');
    campoRef.current?.focus();
  };

  // Cómo quedan todas las propiedades con esta editada: lo que cuenta el pie y lo que se aplica.
  const resultado = useMemo<Propiedad[]>(() => {
    const esta: Propiedad = { nombre: nombre.trim(), valores };
    if (indice === null) return [...propiedades, esta];
    return propiedades.map((p, i) => (i === indice ? esta : p));
  }, [propiedades, indice, nombre, valores]);

  const total = cuantasVariantes(resultado);
  const pasaElTope = maxVariantes !== null && total > maxVariantes;
  const quitados = (actual?.valores ?? []).filter((o) => !valores.some((v) => v.original === o.original)).length;
  const sinCambios =
    actual !== null &&
    actual.nombre === nombre.trim() &&
    actual.valores.length === valores.length &&
    actual.valores.every((o, i) => o.texto === valores[i]?.texto);

  const aplicar = async () => {
    if (nombre.trim() === '' || valores.length === 0 || pasaElTope || aplicando) return;
    setAplicando(true);
    aplicandoRef.current = true;
    setErrorAlAplicar(null);
    const error = await onAplicar(resultado);
    aplicandoRef.current = false;
    setAplicando(false);
    if (error) {
      setErrorAlAplicar(error);
      return;
    }
    onCerrar();
  };

  const mover = (desde: number, hasta: number) =>
    setValores((vs) => {
      if (desde === hasta) return vs;
      const copia = [...vs];
      const [x] = copia.splice(desde, 1);
      copia.splice(hasta, 0, x!);
      return copia;
    });

  const coloresAMostrar = busquedaColor.trim() ? buscarColores(busquedaColor, 24) : COLORES_COMUNES;

  // En un portal: dentro de la ficha quedaba atrapado en su contexto de apilamiento y,
  // en el teléfono, la barra de navegación de abajo le tapaba el botón de crear.
  return createPortal(
    <div className="fixed inset-0 z-[1000] flex justify-end" role="dialog" aria-modal="true" aria-labelledby="titulo-propiedad">
      <button type="button" aria-label="Cerrar" onClick={cerrar} className="absolute inset-0 bg-black/40" />
      <div className="relative flex h-full w-full max-w-[480px] flex-col bg-white shadow-2xl">
        {/* ── Encabezado ── */}
        <div className="flex items-center gap-2 border-b border-neutral-100 py-3 px-4">
          {!eligiendoNombre && indice === null && !nombreInicial ? (
            <button
              type="button"
              onClick={() => setEligiendoNombre(true)}
              aria-label="Elegir otra propiedad"
              className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : null}
          <h2 id="titulo-propiedad" className="min-w-0 flex-1 truncate text-base font-semibold text-brand-black">
            {eligiendoNombre ? 'Nueva propiedad' : nombre}
          </h2>
          <button
            type="button"
            onClick={cerrar}
            aria-label="Cerrar"
            className="grid h-8 w-8 place-items-center rounded-lg text-neutral-500 hover:bg-neutral-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {eligiendoNombre ? (
          // ── Paso 0: qué propiedad (sólo si no vino nombrada) ──
          <div className="flex-1 overflow-y-auto p-5">
            <p className="text-sm text-neutral-600">¿En qué cambia este producto de una variante a otra?</p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              {sugeridos.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => {
                    setNombre(s);
                    setEligiendoNombre(false);
                  }}
                  className="rounded-xl border border-neutral-200 py-3 px-4 text-left text-sm font-medium text-brand-black transition-colors hover:border-[#1644EB] hover:bg-[#F5F7FF]"
                >
                  {s}
                </button>
              ))}
            </div>
            <form
              className="mt-5"
              onSubmit={(e) => {
                e.preventDefault();
                if (nombre.trim()) setEligiendoNombre(false);
              }}
            >
              <label className="text-xs font-medium text-neutral-500" htmlFor="nombre-propiedad">
                Otra
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  id="nombre-propiedad"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder="Ej: Sabor, Medida, Aroma"
                  className="min-w-0 flex-1 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm outline-none focus:border-[#1644EB]"
                />
                <button type="submit" disabled={!nombre.trim()} className={boton('primario', { chico: true })}>
                  Seguir
                </button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              <div className="p-5 pb-3">
                <p className="text-sm text-neutral-600">
                  Elegí todos los {queSon} que vas a ofrecer. Cada uno es una variante
                  {propiedades.length > (indice === null ? 0 : 1) ? ', combinada con las otras propiedades' : ''}.
                </p>

                {/* Escribir: uno o varios separados por coma. */}
                <form
                  className="mt-4 flex gap-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    agregarEscrito();
                  }}
                >
                  <input
                    ref={campoRef}
                    autoFocus={!esTalle && !esColor}
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    placeholder={esTalle ? 'Otro talle, ej: XXXL o 3, 5' : esColor ? 'Otro color' : `Ej: ${unoDe} 1, ${unoDe} 2`}
                    aria-label={`Agregar ${unoDe}`}
                    className="min-w-0 flex-1 rounded-xl border border-neutral-200 py-2.5 px-3 text-sm outline-none focus:border-[#1644EB]"
                  />
                  <button type="submit" disabled={!texto.trim()} className={boton('secundario', { chico: true })}>
                    <Plus className="h-4 w-4" /> Agregar
                  </button>
                </form>
                <p className="mt-1.5 text-[11px] text-neutral-400">Podés pegar varios separados por coma.</p>
              </div>

              {/* ── Los elegidos: el orden es el del selector de la tienda ── */}
              {valores.length > 0 && (
                <div className="border-t border-neutral-100 py-3 px-5">
                  <div className="flex items-baseline justify-between">
                    <p className="text-xs font-semibold text-brand-black">
                      Elegidos <span className="font-normal text-neutral-400">· {valores.length}</span>
                    </p>
                    <p className="text-[11px] text-neutral-400">Arrastrá para ordenar</p>
                  </div>
                  <ul className="mt-2 space-y-1.5">
                    {valores.map((v, i) => {
                      const muestra = esColor && !colorComoTexto ? colorDeTiendanube(v.texto) : null;
                      return (
                        <li
                          key={(v.original ?? 'n') + i}
                          draggable
                          onDragStart={() => setArrastrando(i)}
                          onDragOver={(e) => {
                            e.preventDefault();
                            if (arrastrando !== null && arrastrando !== i) {
                              mover(arrastrando, i);
                              setArrastrando(i);
                            }
                          }}
                          onDragEnd={() => setArrastrando(null)}
                          className={`flex items-center gap-2 rounded-xl border bg-white py-1 pl-1.5 pr-1 ${
                            arrastrando === i ? 'border-[#1644EB] shadow-sm' : 'border-neutral-200'
                          }`}
                        >
                          <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-neutral-300" aria-hidden="true" />
                          {esColor && !colorComoTexto && (
                            <span
                              className="h-4 w-4 shrink-0 rounded-full border border-black/10"
                              style={{ backgroundColor: muestra?.hex ?? 'transparent' }}
                              aria-hidden="true"
                            />
                          )}
                          <input
                            value={v.texto}
                            onChange={(e) =>
                              setValores((vs) => vs.map((x, j) => (j === i ? { ...x, texto: e.target.value } : x)))
                            }
                            aria-label={`${unoDe} ${i + 1}`}
                            className="min-w-0 flex-1 rounded-lg py-1.5 px-1.5 text-sm text-brand-black outline-none focus:bg-neutral-50"
                          />
                          <button
                            type="button"
                            onClick={() => setValores((vs) => vs.filter((_, j) => j !== i))}
                            aria-label={`Quitar ${v.texto}`}
                            className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {/* ── Sugeridos: un toque elige o desmarca ── */}
              {esTalle &&
                TALLES.map((g) => {
                  const todos = g.valores.every(elegido);
                  return (
                    <div key={g.grupo} className="border-t border-neutral-100 py-3 px-5">
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-semibold text-brand-black">{g.grupo}</p>
                        <button
                          type="button"
                          onClick={() =>
                            todos
                              ? setValores((vs) => vs.filter((x) => !g.valores.some((t) => mismoValor(t, x.texto))))
                              : sumar(g.valores)
                          }
                          className="text-xs font-medium text-[#1644EB] hover:underline"
                        >
                          {todos ? 'Quitar todos' : 'Elegir todos'}
                        </button>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {g.valores.map((t) => (
                          <Pill key={t} activa={elegido(t)} onClick={() => alternar(t)}>
                            {t}
                          </Pill>
                        ))}
                      </div>
                    </div>
                  );
                })}

              {esColor && (
                <div className="border-t border-neutral-100 py-3 px-5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-xs font-semibold text-brand-black">
                      {busquedaColor.trim() ? 'Colores de Tienda Nube' : 'Los más usados'}
                    </p>
                    <label className="flex items-center gap-1.5 rounded-lg border border-neutral-200 py-1 px-2">
                      <Search className="h-3.5 w-3.5 text-neutral-400" />
                      <input
                        value={busquedaColor}
                        onChange={(e) => setBusquedaColor(e.target.value)}
                        placeholder="Buscar color"
                        aria-label="Buscar color"
                        className="w-28 text-xs outline-none"
                      />
                    </label>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {coloresAMostrar.map((c) => (
                      <Pill key={c.nombre} activa={elegido(c.nombre)} onClick={() => alternar(c.nombre)}>
                        <span
                          className="h-3.5 w-3.5 rounded-full border border-black/10"
                          style={{ backgroundColor: c.hex }}
                          aria-hidden="true"
                        />
                        {c.nombre}
                      </Pill>
                    ))}
                    {coloresAMostrar.length === 0 && (
                      <p className="text-xs text-neutral-500">
                        No está en la lista de Tienda Nube: agregalo arriba y se va a ver como texto.
                      </p>
                    )}
                  </div>
                  {!colorComoTexto && (
                    <p className="mt-2 text-[11px] text-neutral-400">
                      Los de esta lista se ven como círculo de color en la tienda.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* ── Pie: qué va a pasar, antes de que pase ── */}
            <div className="border-t border-neutral-100 bg-white p-4">
              {errorAlAplicar && (
                <p role="alert" className="mb-3 rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">
                  {errorAlAplicar}
                </p>
              )}
              <p className={`mb-3 text-xs ${pasaElTope ? 'text-red-600' : 'text-neutral-500'}`}>
                {valores.length === 0
                  ? `Elegí al menos un ${unoDe}.`
                  : pasaElTope
                    ? `Serían ${total} variantes y el destino admite hasta ${maxVariantes}. Sacá algunos ${queSon}.`
                    : resumen(total, variantesAntes, quitados, unoDe)}
              </p>
              <div className="flex gap-2">
                <button type="button" onClick={cerrar} className={boton('neutro', { extra: 'flex-1' })}>
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void aplicar()}
                  disabled={valores.length === 0 || pasaElTope || aplicando || sinCambios}
                  className={boton('primario', { extra: 'flex-[2]' })}
                >
                  {aplicando ? 'Guardando…' : indice === null ? `Crear ${total} variante${total === 1 ? '' : 's'}` : 'Guardar'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

function resumen(total: number, antes: number, quitados: number, unoDe: string): string {
  const base = `Vas a tener ${total} variante${total === 1 ? '' : 's'}.`;
  if (quitados > 0) {
    return `${base} Al quitar ${quitados === 1 ? `un ${unoDe}` : `${quitados} ${unoDe}s`} se borran sus variantes, con su stock y sus fotos.`;
  }
  if (antes > 0 && total > antes) return `${base} Las que ya tenías conservan su stock, precio y fotos.`;
  return base;
}

function Pill({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={`inline-flex min-w-[44px] items-center justify-center gap-1.5 rounded-full border py-1.5 px-3 text-sm transition-colors ${
        activa
          ? 'border-[#1644EB] bg-[#1644EB] text-white'
          : 'border-neutral-200 bg-white text-brand-black hover:border-neutral-300 hover:bg-neutral-50'
      }`}
    >
      {activa && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
      {children}
    </button>
  );
}
